import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type {
  OptionGroupDetail,
  OptionChoiceRecord,
  OptionChoicePriceRecord,
  OptionSelectionType,
} from "../../services/options";
import {
  upsertOptionGroup,
  deleteOptionGroup,
  upsertOptionChoice,
  deleteOptionChoice,
  upsertOptionGroupTarget,
  deleteOptionGroupTarget,
  upsertOptionChoicePrice,
  deleteOptionChoicePrice,
} from "../../services/options";

import type {
  SizeTemplateRecord,
  ItemGroupRecord,
  CategoryRecord,
  ProductRecord,
} from "../../services/catalog";

const generalSchema = z.object({
  name: z.string().min(2, "İsim zorunludur"),
  slug: z.string().min(1, "Slug zorunludur"),
});

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const formatNameFromSlug = (slug: string) => {
  if (!slug) return "Opsiyon grubu";
  return slug
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const defaultTargetFactory = () => ({
  itemGroupId: "",
  categoryId: "",
  productId: "",
  priority: 100,
  isRequiredOverride: false,
});

type GeneralFormValues = z.infer<typeof generalSchema>;

type ChoiceFormState = {
  name: string;
  priceDelta: string;
};

const createChoiceDraft = (): ChoiceFormState => ({
  name: "",
  priceDelta: "",
});

type TargetDraft = {
  itemGroupId: string;
  categoryId: string;
  productId: string;
  priority: number;
  isRequiredOverride: boolean;
};

type GroupSettingsState = {
  description: string;
  selectionType: OptionSelectionType;
  isRequired: boolean;
  minSelections: number;
  maxSelections: number | null;
  priority: number;
};

interface OptionGroupEditorProps {
  optionGroup: OptionGroupDetail | null;
  isCreating: boolean;
  sizeTemplates: SizeTemplateRecord[];
  itemGroups: ItemGroupRecord[];
  categories: CategoryRecord[];
  products: ProductRecord[];
  onSaved: (groupId: string) => void;
  onCreated: (groupId: string) => void;
  onDeleted: () => void;
}

const ChoicePriceRow = ({
  price,
  sizeLabel,
  onSave,
  onDelete,
}: {
  price: OptionChoicePriceRecord;
  sizeLabel: string;
  onSave: (amount: number, isAvailable: boolean) => void;
  onDelete: () => void;
}) => {
  const [amountInput, setAmountInput] = useState<string>(() =>
    (price.priceOverride ?? price.priceDelta ?? 0).toString(),
  );
  const [available, setAvailable] = useState<boolean>(
    price.isAvailable ?? true,
  );

  useEffect(() => {
    setAmountInput((price.priceOverride ?? price.priceDelta ?? 0).toString());
    setAvailable(price.isAvailable ?? true);
  }, [price]);

  const parsedAmount = Number(amountInput);
  const normalizedAmount = Number.isFinite(parsedAmount) ? parsedAmount : 0;
  const currentAmount = price.priceOverride ?? price.priceDelta ?? 0;
  const dirty =
    (Number.isFinite(parsedAmount) && normalizedAmount !== currentAmount) ||
    available !== (price.isAvailable ?? true);

  return (
    <tr>
      <td>{sizeLabel}</td>
      <td>
        <input
          type="number"
          step={0.5}
          value={amountInput}
          onChange={(event) => setAmountInput(event.target.value)}
        />
      </td>
      <td>
        <label className="toggle">
          <input
            type="checkbox"
            checked={available}
            onChange={(event) => setAvailable(event.target.checked)}
          />
          <span>{available ? "Aktif" : "Pasif"}</span>
        </label>
      </td>
      <td>
        <button
          type="button"
          className="button secondary"
          disabled={!dirty || !Number.isFinite(parsedAmount)}
          onClick={() => onSave(normalizedAmount, available)}
        >
          Kaydet
        </button>
      </td>
      <td>
        <button type="button" className="button ghost" onClick={onDelete}>
          Sil
        </button>
      </td>
    </tr>
  );
};

const ProductSelectionList = ({
  products,
  selectedIds,
  onToggle,
  onSelectAll,
  onClearAll,
}: {
  products: ProductRecord[];
  selectedIds: string[];
  onToggle: (productId: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
}) => {
  if (products.length === 0) {
    return null;
  }

  return (
    <div className="product-selection">
      <div className="product-selection__header">
        <span>
          {selectedIds.length}/{products.length} ürün seçildi
        </span>
        <div className="product-selection__actions">
          <button type="button" className="button ghost" onClick={onSelectAll}>
            Tümünü işaretle
          </button>
          <button type="button" className="button ghost" onClick={onClearAll}>
            Temizle
          </button>
        </div>
      </div>
      <div className="product-selection__list">
        {products.map((product) => (
          <label key={product.id} className="product-selection__item">
            <input
              type="checkbox"
              checked={selectedIds.includes(product.id)}
              onChange={() => onToggle(product.id)}
            />
            <span>{product.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const ChoiceRow = ({
  choice,
  sizeTemplates,
  onSaveName,
  onDelete,
  onSavePrice,
  onDeletePrice,
  onCreatePrice,
}: {
  choice: OptionChoiceRecord;
  sizeTemplates: SizeTemplateRecord[];
  onSaveName: (name: string, priceDelta: number) => void;
  onDelete: () => void;
  onSavePrice: (
    price: OptionChoicePriceRecord,
    amount: number,
    isAvailable: boolean,
  ) => void;
  onDeletePrice: (priceId: string) => void;
  onCreatePrice: (sizeTemplateId: string, amount: number) => void;
}) => {
  const [formState, setFormState] = useState<ChoiceFormState>({
    name: choice.name,
    priceDelta: choice.priceDelta.toString(),
  });
  const [newPriceTemplateId, setNewPriceTemplateId] = useState<string>("");
  const [newPriceAmount, setNewPriceAmount] = useState<string>("");
  const sizeTemplateLookup = useMemo(
    () => Object.fromEntries(sizeTemplates.map((template) => [template.id, template])),
    [sizeTemplates],
  );
  const assignedTemplateIds = useMemo(
    () =>
      new Set(
        choice.prices
          .map((price) => price.sizeTemplateId)
          .filter((value): value is string => Boolean(value)),
      ),
    [choice.prices],
  );
  const availableTemplateOptions = useMemo(
    () => sizeTemplates.filter((template) => !assignedTemplateIds.has(template.id)),
    [sizeTemplates, assignedTemplateIds],
  );
  const sortedPrices = useMemo(() => {
    return [...choice.prices].sort((a, b) => {
      const labelA =
        sizeTemplateLookup[a.sizeTemplateId ?? ""]?.name ??
        a.sizeTemplateId ??
        a.sizeCode ??
        "";
      const labelB =
        sizeTemplateLookup[b.sizeTemplateId ?? ""]?.name ??
        b.sizeTemplateId ??
        b.sizeCode ??
        "";
      return labelA.localeCompare(labelB, "tr", { sensitivity: "base" });
    });
  }, [choice.prices, sizeTemplateLookup]);

  useEffect(() => {
    setFormState({
      name: choice.name,
      priceDelta: choice.priceDelta.toString(),
    });
    setNewPriceTemplateId(availableTemplateOptions[0]?.id ?? "");
    setNewPriceAmount("");
  }, [choice, availableTemplateOptions]);

  const priceDeltaInput = formState.priceDelta.trim();
  const parsedPriceDelta =
    priceDeltaInput === "" ? 0 : Number(priceDeltaInput);
  const isPriceDeltaValid =
    priceDeltaInput === "" || Number.isFinite(parsedPriceDelta);
  const normalizedPriceDelta = isPriceDeltaValid ? parsedPriceDelta : choice.priceDelta;

  const hasChanges = useMemo(() => {
    return (
      formState.name.trim() !== choice.name.trim() ||
      normalizedPriceDelta !== choice.priceDelta
    );
  }, [choice, formState, normalizedPriceDelta]);

  const basePriceLabel =
    choice.priceDelta > 0 ? `+${choice.priceDelta}` : `${choice.priceDelta}`;

  return (
    <div className="option-choice-row">
      <div className="choice-fields">
        <div className="form-grid two-columns">
          <label className="form-control">
            <span>Ad</span>
            <input
              value={formState.name}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, name: event.target.value }))
              }
            />
          </label>
          <label className="form-control">
            <span>Fiyat farkı (₺)</span>
            <input
              type="number"
              step={0.5}
              placeholder="0"
              value={formState.priceDelta}
              onChange={(event) =>
                setFormState((prev) => ({ ...prev, priceDelta: event.target.value }))
              }
            />
            <p className="helper-text">
              0 ise fiyat değişmez. Pozitif değerler müşteri ekranında + olarak görünür.
            </p>
          </label>
        </div>
        <div className="form-actions inline">
          <button
            type="button"
            className="button secondary"
            disabled={!hasChanges || !formState.name.trim() || !isPriceDeltaValid}
            onClick={() => onSaveName(formState.name, normalizedPriceDelta)}
          >
            Kaydet
          </button>
          <button
            type="button"
            className="button ghost"
            onClick={() =>
              setFormState({
                name: choice.name,
                priceDelta: choice.priceDelta.toString(),
              })
            }
          >
            Sıfırla
          </button>
          <button type="button" className="button danger" onClick={onDelete}>
            Sil
          </button>
        </div>
        <div className="choice-price-table">
          {choice.prices.length === 0 ? (
            <p className="helper-text">
              Bu seçenek için boyut bazlı fiyat bulunamadı. Varsayılan fiyat farkı{" "}
              {basePriceLabel} ₺.
            </p>
          ) : (
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Boyut</th>
                    <th>Fiyat (₺)</th>
                    <th>Durum</th>
                    <th colSpan={2}>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPrices.map((price) => {
                    const template = price.sizeTemplateId
                      ? sizeTemplateLookup[price.sizeTemplateId]
                      : null;
                    const sizeLabel =
                      template?.name ??
                      template?.slug ??
                      (price.sizeCode ? `${price.sizeCode.toUpperCase()} boyutu` : "Boyut");
                    return (
                      <ChoicePriceRow
                        key={price.id}
                        price={price}
                        sizeLabel={sizeLabel}
                        onSave={(amount, isAvailable) =>
                          onSavePrice(price, amount, isAvailable)
                        }
                        onDelete={() => onDeletePrice(price.id)}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {availableTemplateOptions.length > 0 ? (
            <form
              className="form inline-form"
              onSubmit={(event) => {
                event.preventDefault();
                if (!newPriceTemplateId) return;
                const parsedAmount = Number(newPriceAmount);
                if (!Number.isFinite(parsedAmount)) return;
                onCreatePrice(newPriceTemplateId, parsedAmount);
                setNewPriceAmount("");
              }}
            >
              <div className="form-grid two-columns">
                <label className="form-control">
                  <span>Boyut</span>
                  <select
                    value={newPriceTemplateId}
                    onChange={(event) => setNewPriceTemplateId(event.target.value)}
                  >
                    {availableTemplateOptions.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="form-control">
                  <span>Fiyat (₺)</span>
                  <input
                    type="number"
                    step={0.5}
                    value={newPriceAmount}
                    onChange={(event) => setNewPriceAmount(event.target.value)}
                  />
                </label>
              </div>
              <div className="form-actions">
                <button
                  type="submit"
                  className="button secondary"
                  disabled={!newPriceTemplateId || !newPriceAmount.trim()}
                >
                  Fiyat ekle
                </button>
              </div>
            </form>
          ) : (
            <p className="helper-text">
              Bu seçenek için tüm boyut fiyatları tanımlı.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const TargetsTable = ({
  targets,
  itemGroups,
  categories,
  products,
  sizeTemplates,
  onDelete,
}: {
  targets: OptionGroupDetail["targets"];
  itemGroups: ItemGroupRecord[];
  categories: CategoryRecord[];
  products: ProductRecord[];
  sizeTemplates: SizeTemplateRecord[];
  onDelete: (targetId: string) => void;
}) => {
  const groupMap = useMemo(
    () => Object.fromEntries(itemGroups.map((group) => [group.id, group])),
    [itemGroups],
  );
  const categoryMap = useMemo(
    () => Object.fromEntries(categories.map((category) => [category.id, category])),
    [categories],
  );
  const productMap = useMemo(
    () => Object.fromEntries(products.map((product) => [product.id, product])),
    [products],
  );
  const sizeTemplateMap = useMemo(
    () => Object.fromEntries(sizeTemplates.map((template) => [template.id, template])),
    [sizeTemplates],
  );

  if (targets.length === 0) {
    return <p className="helper-text">Bu gruba bağlı hedef yok.</p>;
  }

  return (
    <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th>Item Group</th>
            <th>Kategori</th>
            <th>Ürün</th>
            <th>Pizza Boyutu</th>
            <th>Öncelik</th>
            <th>Zorunlu</th>
            <th>İşlem</th>
          </tr>
        </thead>
        <tbody>
          {targets.map((target) => (
            <tr key={target.id}>
              <td>{(target.itemGroupId && groupMap[target.itemGroupId]?.name) ?? "-"}</td>
              <td>
                {(target.menuCategoryId && categoryMap[target.menuCategoryId]?.name) ??
                  "-"}
              </td>
              <td>{(target.productId && productMap[target.productId]?.name) ?? "-"}</td>
              <td>
                {target.sizeTemplateId
                  ? sizeTemplateMap[target.sizeTemplateId]?.name ?? target.sizeTemplateId
                  : "Tümü"}
              </td>
              <td>{target.priority}</td>
              <td>{target.isRequiredOverride != null ? (target.isRequiredOverride ? "Evet" : "Hayır") : "Varsayılan"}</td>
              <td>
                <button
                  type="button"
                  className="button danger"
                  onClick={() => onDelete(target.id)}
                >
                  Sil
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function OptionGroupEditor({
  optionGroup,
  isCreating,
  sizeTemplates,
  itemGroups,
  categories,
  products,
  onSaved,
  onCreated,
  onDeleted,
}: OptionGroupEditorProps) {
  const queryClient = useQueryClient();
  const [newChoices, setNewChoices] = useState<ChoiceFormState[]>(() => [
    createChoiceDraft(),
  ]);
  const [targetDraft, setTargetDraft] = useState<TargetDraft>(() => defaultTargetFactory());
  const [selectedSizeTemplateIds, setSelectedSizeTemplateIds] = useState<string[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);

  const invalidateDetail = () => {
    if (optionGroup) {
      void queryClient.invalidateQueries({
        queryKey: ["options", "group-detail", optionGroup.id],
      });
    }
  };

  const defaultValues: GeneralFormValues = useMemo(
    () => ({ name: optionGroup?.name ?? "", slug: optionGroup?.slug ?? "" }),
    [optionGroup?.name, optionGroup?.slug],
  );
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<GeneralFormValues>({
    resolver: zodResolver(generalSchema),
    defaultValues,
    values: defaultValues,
  });
  const nameValue = watch("name") ?? "";
  const slugValue = watch("slug") ?? "";
  const deriveGroupSettings = useCallback((): GroupSettingsState => ({
    description: optionGroup?.description ?? "",
    selectionType: optionGroup?.selectionType ?? "single",
    isRequired: optionGroup?.isRequired ?? false,
    minSelections: optionGroup?.minSelections ?? 0,
    maxSelections: optionGroup?.maxSelections ?? null,
    priority: optionGroup?.priority ?? 100,
  }), [optionGroup]);
  const [groupSettings, setGroupSettings] = useState<GroupSettingsState>(
    () => deriveGroupSettings(),
  );

  useEffect(() => {
    setGroupSettings(deriveGroupSettings());
  }, [deriveGroupSettings]);

  const upsertGroupMutation = useMutation({
    mutationFn: upsertOptionGroup,
    onSuccess: (record) => {
      void queryClient.invalidateQueries({ queryKey: ["options", "groups"] });
      if (optionGroup) {
        onSaved(record.id);
      } else if (isCreating) {
        onCreated(record.id);
      }
      invalidateDetail();
    },
  });

  const deleteGroupMutation = useMutation({
    mutationFn: deleteOptionGroup,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["options", "groups"] });
      onDeleted();
    },
  });

  const upsertChoiceMutation = useMutation({
    mutationFn: upsertOptionChoice,
    onSuccess: () => {
      invalidateDetail();
    },
  });

  const deleteChoiceMutation = useMutation({
    mutationFn: deleteOptionChoice,
    onSuccess: () => invalidateDetail(),
  });

  const upsertChoicePriceMutation = useMutation({
    mutationFn: upsertOptionChoicePrice,
    onSuccess: () => invalidateDetail(),
  });

  const deleteChoicePriceMutation = useMutation({
    mutationFn: deleteOptionChoicePrice,
    onSuccess: () => invalidateDetail(),
  });


  const upsertTargetMutation = useMutation({
    mutationFn: upsertOptionGroupTarget,
    onSuccess: () => {
      invalidateDetail();
      setTargetDraft(defaultTargetFactory());
    },
  });

  const deleteTargetMutation = useMutation({
    mutationFn: deleteOptionGroupTarget,
    onSuccess: () => invalidateDetail(),
  });

  const onSubmit = (values: GeneralFormValues) => {
    const trimmedName = values.name.trim();
    const normalizedSlug = slugify(values.slug || trimmedName) || slugify(trimmedName);
    if (!normalizedSlug) {
      return Promise.reject(new Error("Slug oluşturulamadı. Lütfen geçerli bir isim girin."));
    }
    const minSelections = Math.max(0, groupSettings.minSelections ?? 0);
    const maxSelections =
      groupSettings.selectionType === "single"
        ? 1
        : groupSettings.maxSelections != null
          ? Math.max(minSelections, groupSettings.maxSelections)
          : null;
    return upsertGroupMutation.mutateAsync({
      id: optionGroup?.id,
      name: trimmedName || formatNameFromSlug(values.slug),
      slug: normalizedSlug,
      description: groupSettings.description,
      selectionType: groupSettings.selectionType,
      isRequired: groupSettings.isRequired,
      minSelections,
      maxSelections,
      priority: groupSettings.priority,
    });
  };

  const handleChoiceSave = (
    choiceId: string,
    nameValue: string,
    priceDelta: number,
  ) => {
    if (!optionGroup) return;
    const trimmedName = nameValue.trim();
    if (!trimmedName) return;
    const existing = optionGroup.choices.find((entry) => entry.id === choiceId);
    upsertChoiceMutation.mutate({
      id: choiceId,
      optionGroupId: optionGroup.id,
      name: trimmedName,
      slug: slugify(trimmedName) || undefined,
      priceDelta,
      sortOrder: existing?.sortOrder ?? 0,
    });
  };

  const handleSaveChoicePrice = (
    choiceId: string,
    price: OptionChoicePriceRecord,
    amount: number,
    isAvailable: boolean,
  ) => {
    if (!optionGroup) return;
    upsertChoicePriceMutation.mutate({
      id: price.id,
      optionChoiceId: choiceId,
      sizeTemplateId: price.sizeTemplateId ?? undefined,
      productSizeId: price.productSizeId ?? undefined,
      sizeCode: price.sizeCode ?? undefined,
      priceDelta: amount,
      isAvailable,
    });
  };

  const handleCreateChoicePrice = (
    choiceId: string,
    sizeTemplateId: string,
    amount: number,
  ) => {
    if (!optionGroup) return;
    upsertChoicePriceMutation.mutate({
      optionChoiceId: choiceId,
      sizeTemplateId,
      priceDelta: amount,
    });
  };

  const handleDeleteChoicePrice = (priceId: string) => {
    deleteChoicePriceMutation.mutate(priceId);
  };

  const handleNewChoiceSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!optionGroup) return;
    const parsedEntries: { name: string; priceDelta: number }[] = [];
    for (let index = 0; index < newChoices.length; index += 1) {
      const choice = newChoices[index];
      const rawNames = choice.name
        .split(/[\n,;]+/)
        .map((value) => value.trim())
        .filter(Boolean);
      if (rawNames.length === 0) continue;
      const priceInput = choice.priceDelta.trim();
      const parsedPrice = priceInput === "" ? 0 : Number(priceInput);
      if (priceInput !== "" && !Number.isFinite(parsedPrice)) {
        window.alert(`Fiyat farkı ${index + 1}. satırda geçersiz.`);
        return;
      }
      rawNames.forEach((name) => {
        parsedEntries.push({ name, priceDelta: parsedPrice });
      });
    }
    if (parsedEntries.length === 0) return;
    const seen = new Set<string>();
    const uniqueEntries = parsedEntries.filter((entry) => {
      const key = entry.name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    let sortOrder = optionGroup.choices.length;
    try {
      for (const entry of uniqueEntries) {
        await upsertChoiceMutation.mutateAsync({
          optionGroupId: optionGroup.id,
          name: entry.name,
          slug: slugify(entry.name) || undefined,
          priceDelta: entry.priceDelta ?? 0,
          sortOrder,
        });
        sortOrder += 1;
      }
      setNewChoices([createChoiceDraft()]);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Seçenekler eklenirken hata oluştu.";
      window.alert(message);
    }
  };

  const handleAddTarget = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!optionGroup) return;

    const templateIds = selectedSizeTemplateIds.length
      ? selectedSizeTemplateIds
      : [null];
    const resolvedProductIds =
      targetDraft.productId && targetDraft.productId.length > 0
        ? [targetDraft.productId]
        : selectedProductIds;

    const hasGroupScope =
      Boolean(targetDraft.itemGroupId) || Boolean(targetDraft.categoryId);

    if (
      hasGroupScope &&
      scopedProducts.length > 0 &&
      !targetDraft.productId &&
      resolvedProductIds.length === 0
    ) {
      window.alert("Bu grup için en az bir ürün seçmelisiniz.");
      return;
    }

    if (
      !hasGroupScope &&
      !targetDraft.productId &&
      resolvedProductIds.length === 0 &&
      templateIds[0] === null
    ) {
      window.alert("En az bir hedef veya boyut seçin");
      return;
    }

    const payloadBase = {
      optionGroupId: optionGroup.id,
      itemGroupId: targetDraft.itemGroupId || null,
      menuCategoryId: targetDraft.categoryId || null,
      priority: targetDraft.priority,
      isRequiredOverride: targetDraft.isRequiredOverride,
    };

    const combos: { productId: string | null; sizeTemplateId: string | null }[] =
      [];

    if (resolvedProductIds.length > 0) {
      resolvedProductIds.forEach((productId) => {
        templateIds.forEach((sizeTemplateId) => {
          combos.push({ productId, sizeTemplateId });
        });
      });
    } else {
      templateIds.forEach((sizeTemplateId) => {
        combos.push({ productId: null, sizeTemplateId });
      });
    }

    await Promise.all(
      combos.map(({ productId, sizeTemplateId }) =>
        upsertTargetMutation.mutateAsync({
          ...payloadBase,
          productId,
          sizeTemplateId,
        }),
      ),
    );

    setTargetDraft(defaultTargetFactory());
    setSelectedSizeTemplateIds([]);
    setSelectedProductIds([]);
  };

  useEffect(() => {
    setNewChoices([createChoiceDraft()]);
    setTargetDraft(defaultTargetFactory());
    setSelectedSizeTemplateIds([]);
  }, [optionGroup]);

  const categoriesByGroup = useMemo(() => {
    return categories.reduce<Record<string, CategoryRecord[]>>((acc, category) => {
      acc[category.groupId] = acc[category.groupId] || [];
      acc[category.groupId]!.push(category);
      return acc;
    }, {});
  }, [categories]);

  const categorySlugToId = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((category) => {
      map.set(category.slug, category.id);
    });
    return map;
  }, [categories]);

  const productsByCategoryId = useMemo(() => {
    return products.reduce<Record<string, ProductRecord[]>>((acc, product) => {
      const categoryId = categorySlugToId.get(product.category);
      if (!categoryId) {
        return acc;
      }
      acc[categoryId] = acc[categoryId] || [];
      acc[categoryId]!.push(product);
      return acc;
    }, {});
  }, [products, categorySlugToId]);

  const scopedProducts = useMemo(() => {
    if (targetDraft.categoryId) {
      return productsByCategoryId[targetDraft.categoryId] ?? [];
    }
    if (targetDraft.itemGroupId) {
      const categoriesForGroup = categoriesByGroup[targetDraft.itemGroupId] ?? [];
      const aggregated: ProductRecord[] = [];
      categoriesForGroup.forEach((category) => {
        aggregated.push(...(productsByCategoryId[category.id] ?? []));
      });
      return aggregated;
    }
    return [];
  }, [targetDraft.categoryId, targetDraft.itemGroupId, categoriesByGroup, productsByCategoryId]);

  const scopeKey = targetDraft.productId
    ? `product:${targetDraft.productId}`
    : targetDraft.categoryId
      ? `category:${targetDraft.categoryId}`
      : targetDraft.itemGroupId
        ? `group:${targetDraft.itemGroupId}`
        : "none";
  const scopeKeyRef = useRef(scopeKey);

  useEffect(() => {
    if (scopeKey !== scopeKeyRef.current) {
      scopeKeyRef.current = scopeKey;
      if (!targetDraft.productId && scopedProducts.length > 0) {
        setSelectedProductIds(scopedProducts.map((product) => product.id));
      } else {
        setSelectedProductIds([]);
      }
      return;
    }

    if (targetDraft.productId) {
      setSelectedProductIds([]);
      return;
    }

    setSelectedProductIds((prev) => {
      if (prev.length === 0) return prev;
      const allowed = new Set(scopedProducts.map((product) => product.id));
      const next = prev.filter((id) => allowed.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [scopeKey, scopedProducts, targetDraft.productId]);

  const handleToggleProductSelection = (productId: string) => {
    setSelectedProductIds((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId],
    );
  };

  const handleSelectAllProducts = () => {
    setSelectedProductIds(scopedProducts.map((product) => product.id));
  };

  const handleClearAllProducts = () => {
    setSelectedProductIds([]);
  };

  const displayName =
    nameValue ||
    optionGroup?.name ||
    formatNameFromSlug(slugValue || optionGroup?.slug || "");
  const showAdvancedSections = Boolean(optionGroup);
  const availableCategories =
    targetDraft.itemGroupId && showAdvancedSections
      ? categoriesByGroup[targetDraft.itemGroupId] ?? []
      : categories;
  const sortedChoices = useMemo(() => {
    if (!optionGroup) return [];
    return [...optionGroup.choices].sort((a, b) =>
      a.name.localeCompare(b.name, "tr", { sensitivity: "base" }),
    );
  }, [optionGroup]);
  const hasInvalidNewChoicePrice = useMemo(() => {
    return newChoices.some((choice) => {
      const input = choice.priceDelta.trim();
      return input !== "" && !Number.isFinite(Number(input));
    });
  }, [newChoices]);

  if (!optionGroup && !isCreating) {
    return (
      <div className="card">
        <div className="card-header">
          <div>
            <p className="card-title">Opsiyon grubu seçin</p>
            <p className="card-subtitle">
              Soldaki listeden bir grup seçin veya yeni grup ekleyin.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <p className="card-title">{displayName}</p>
          <p className="card-subtitle">
            {showAdvancedSections
              ? "İsim ve slug'ı burada düzenleyin."
              : "İsim ve teknik slug alanlarını doldurun."}
          </p>
        </div>
        {showAdvancedSections ? (
          <button
            type="button"
            className="button danger"
            onClick={() => {
              if (
                optionGroup &&
                window.confirm(
                  `${optionGroup.name} opsiyon grubunu silmek istediğinize emin misiniz?`,
                )
              ) {
                deleteGroupMutation.mutate(optionGroup.id);
              }
            }}
          >
            Grubu sil
          </button>
        ) : null}
      </div>

      <div className="option-editor">
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-grid two-columns">
            <label className="form-control">
              <span>İsim (UI'da görünen)</span>
              <input
                type="text"
                {...register("name")}
                placeholder="Örn: İnce Hamur"
              />
              {errors.name ? <p className="input-error">{errors.name.message}</p> : null}
              <p className="helper-text">
                Türkçe karakter kullanabilirsiniz. Uygulamada doğrudan bu isim gösterilir.
              </p>
            </label>

            <label className="form-control">
              <span>Slug</span>
              <input
                type="text"
                {...register("slug")}
                onBlur={(event) => {
                  const value = event.target.value;
                  const next = slugify(value || nameValue);
                  if (next && next !== value) {
                    event.target.value = next;
                    setValue("slug", next, { shouldDirty: true });
                  }
                }}
              />
              {errors.slug ? <p className="input-error">{errors.slug.message}</p> : null}
              <p className="helper-text">
                Örnek: <code>ince-hamur</code>. Türkçe karakter yazabilirsiniz; kayıt esnasında slug
                otomatik olarak temizlenir.
              </p>
            </label>
          </div>

          <div className="form-grid three-columns">
            <label className="form-control">
              <span>Seçim tipi</span>
              <select
                value={groupSettings.selectionType}
                onChange={(event) => {
                  const selectionType = event.target.value as OptionSelectionType;
                  setGroupSettings((prev) => {
                    const clampedMin =
                      selectionType === "single"
                        ? Math.min(prev.minSelections, 1)
                        : prev.minSelections;
                    return {
                      ...prev,
                      selectionType,
                      minSelections: clampedMin,
                      maxSelections: selectionType === "single" ? 1 : prev.maxSelections,
                    };
                  });
                }}
              >
                <option value="single">Tek seçim</option>
                <option value="multi">Çoklu seçim</option>
              </select>
              <p className="helper-text">Tek seçim grupları otomatik olarak 1/1 ile sınırlandırılır.</p>
            </label>
            <label className="form-control">
              <span>Minimum seçim</span>
              <input
                type="number"
                min={0}
                value={groupSettings.minSelections}
                onChange={(event) => {
                  const parsed = Number(event.target.value);
                  const normalized = Number.isFinite(parsed) ? parsed : 0;
                  setGroupSettings((prev) => ({
                    ...prev,
                    minSelections: normalized,
                    maxSelections:
                      prev.maxSelections != null && prev.maxSelections < normalized
                        ? normalized
                        : prev.maxSelections,
                  }));
                }}
              />
              <p className="helper-text">Kullanıcının seçmek zorunda olduğu minimum seçenek sayısı.</p>
            </label>
            <label className="form-control">
              <span>Maksimum seçim</span>
              <input
                type="number"
                min={groupSettings.selectionType === "single" ? 1 : groupSettings.minSelections}
                value={groupSettings.maxSelections ?? ""}
                onChange={(event) => {
                  const raw = event.target.value;
                  if (raw === "") {
                    setGroupSettings((prev) => ({
                      ...prev,
                      maxSelections: prev.selectionType === "single" ? 1 : null,
                    }));
                    return;
                  }
                  const parsed = Number(raw);
                  if (!Number.isFinite(parsed)) return;
                  setGroupSettings((prev) => ({
                    ...prev,
                    maxSelections:
                      prev.selectionType === "single"
                        ? 1
                        : Math.max(prev.minSelections, parsed),
                  }));
                }}
                placeholder="Sınırsız"
              />
              <p className="helper-text">Boş bırakırsanız sınır uygulanmaz.</p>
            </label>
          </div>

          <div className="form-grid two-columns">
            <label className="form-control checkbox">
              <input
                type="checkbox"
                checked={groupSettings.isRequired}
                onChange={(event) =>
                  setGroupSettings((prev) => ({
                    ...prev,
                    isRequired: event.target.checked,
                  }))
                }
              />
              <span>Zorunlu grup</span>
            </label>
            <label className="form-control">
              <span>Öncelik</span>
              <input
                type="number"
                min={0}
                value={groupSettings.priority}
                onChange={(event) => {
                  const parsed = Number(event.target.value);
                  setGroupSettings((prev) => ({
                    ...prev,
                    priority: Number.isFinite(parsed) ? parsed : prev.priority,
                  }));
                }}
              />
              <p className="helper-text">Düşük sayı daha önce gösterilir.</p>
            </label>
          </div>

          <div className="form-actions">
            <button className="button primary" type="submit" disabled={isSubmitting}>
              Kaydet
            </button>
            <button
              type="button"
              className="button ghost"
              onClick={() => {
                reset(optionGroup ? defaultValues : undefined);
                setGroupSettings(deriveGroupSettings());
              }}
            >
              Sıfırla
            </button>
          </div>
        </form>
      </div>

      {showAdvancedSections ? (
        <>
          <div className="option-section">
            <h3>Opsiyon seçenekleri</h3>
            {sortedChoices.length === 0 ? (
              <p className="helper-text">Henüz seçenek eklenmemiş.</p>
            ) : (
              sortedChoices.map((choice: OptionChoiceRecord) => (
                <ChoiceRow
                  key={choice.id}
                  choice={choice}
                  sizeTemplates={sizeTemplates}
                  onSaveName={(name, priceDelta) =>
                    handleChoiceSave(choice.id, name, priceDelta)
                  }
                  onDelete={() => deleteChoiceMutation.mutate(choice.id)}
                  onSavePrice={(price, amount, isAvailable) =>
                    handleSaveChoicePrice(choice.id, price, amount, isAvailable)
                  }
                  onDeletePrice={(priceId) => handleDeleteChoicePrice(priceId)}
                  onCreatePrice={(sizeTemplateId, amount) =>
                    handleCreateChoicePrice(choice.id, sizeTemplateId, amount)
                  }
                />
              ))
            )}

            <form className="form" onSubmit={handleNewChoiceSubmit}>
              <h4>Yeni seçenek ekle</h4>
              {newChoices.map((choice, index) => (
                <div
                  key={`new-choice-${index}`}
                  className="form-row"
                  style={{ alignItems: "flex-end" }}
                >
                  <label className="form-control" style={{ flex: 1 }}>
                    <span>{newChoices.length > 1 ? `Ad ${index + 1}` : "Ad"}</span>
                    <textarea
                      rows={3}
                      placeholder="Örn: Coca-Cola\nFanta\nSprite"
                      value={choice.name}
                      onChange={(event) => {
                        const value = event.target.value;
                        setNewChoices((prev) =>
                          prev.map((item, idx) =>
                            idx === index ? { ...item, name: value } : item,
                          ),
                        );
                      }}
                    />
                  </label>
                  <label className="form-control" style={{ minWidth: "160px" }}>
                    <span>Fiyat farkı (₺)</span>
                    <input
                      type="number"
                      step={0.5}
                      placeholder="0"
                      value={choice.priceDelta}
                      onChange={(event) => {
                        const value = event.target.value;
                        setNewChoices((prev) =>
                          prev.map((item, idx) =>
                            idx === index ? { ...item, priceDelta: value } : item,
                          ),
                        );
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    className="button ghost"
                    disabled={newChoices.length === 1}
                    onClick={() =>
                      setNewChoices((prev) =>
                        prev.filter((_, idx) => idx !== index),
                      )
                    }
                  >
                    Kaldır
                  </button>
                </div>
              ))}
              <div className="form-actions inline">
                <button
                  type="button"
                  className="button ghost"
                  onClick={() =>
                    setNewChoices((prev) => [...prev, createChoiceDraft()])
                  }
                >
                  + Satır ekle
                </button>
              </div>
              <p className="helper-text">
                Birden fazla seçenek eklemek için yeni satır, virgül veya farklı alanlar
                kullanabilirsiniz. Aynı satırdaki tüm isimler aynı fiyat farkını paylaşır.
                Boyut bazlı fiyatlar aşağıdaki tablodan yönetilir. Yeni seçenekler varsayılan
                olarak 0 ₺ ile eklenir ve müşteri ekranında + olarak görünür.
              </p>
              <div className="form-actions">
                <button
                  className="button secondary"
                  type="submit"
                  disabled={
                    newChoices.every((choice) => !choice.name.trim()) ||
                    hasInvalidNewChoicePrice
                  }
                >
                  Seçenek ekle
                </button>
              </div>
            </form>
          </div>

          <div className="option-section">
            <h3>Hedefler</h3>
            <TargetsTable
              targets={optionGroup!.targets}
              itemGroups={itemGroups}
              categories={categories}
              products={products}
              sizeTemplates={sizeTemplates}
              onDelete={(targetId) => deleteTargetMutation.mutate(targetId)}
            />

            <form className="form" onSubmit={handleAddTarget}>
              <h4>Yeni hedef</h4>
              <div className="form-grid two-columns">
            <label className="form-control">
              <span>Item Group</span>
              <select
                value={targetDraft.itemGroupId}
                onChange={(event) =>
                  setTargetDraft((prev) => ({
                    ...prev,
                    itemGroupId: event.target.value,
                    categoryId: "",
                    productId: "",
                  }))
                }
              >
                <option value="">Seçim yok</option>
                {itemGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-control">
              <span>Kategori</span>
              <select
                value={targetDraft.categoryId}
                onChange={(event) =>
                  setTargetDraft((prev) => ({
                    ...prev,
                    categoryId: event.target.value,
                    productId: "",
                  }))
                }
              >
                <option value="">Seçim yok</option>
                {availableCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="form-grid three-columns">
            <label className="form-control">
              <span>Ürün</span>
              <select
                value={targetDraft.productId}
                onChange={(event) =>
                  setTargetDraft((prev) => ({
                    ...prev,
                    productId: event.target.value,
                  }))
                }
              >
                <option value="">Seçim yok</option>
                {(targetDraft.categoryId
                  ? productsByCategoryId[targetDraft.categoryId] ?? []
                  : targetDraft.itemGroupId
                    ? scopedProducts
                    : products
                ).map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="form-control">
              <span>Pizza boyutları</span>
              <select
                multiple
                value={selectedSizeTemplateIds}
                onChange={(event) => {
                  const selected = Array.from(event.target.selectedOptions).map(
                    (option) => option.value,
                  );
                  setSelectedSizeTemplateIds(selected);
                }}
              >
                {sizeTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
              <p className="helper-text">Boş bırakılırsa tüm boyutlarda gösterilir.</p>
            </label>
            <label className="form-control">
              <span>Öncelik</span>
              <input
                    type="number"
                    min={0}
                    value={targetDraft.priority}
                    onChange={(event) =>
                      setTargetDraft((prev) => ({
                        ...prev,
                        priority: Number(event.target.value),
                      }))
                    }
                  />
                </label>
                <label className="form-control checkbox">
                  <input
                    type="checkbox"
                    checked={targetDraft.isRequiredOverride}
                    onChange={(event) =>
                      setTargetDraft((prev) => ({
                        ...prev,
                        isRequiredOverride: event.target.checked,
                      }))
                    }
                  />
                  <span>Zorunluluğu zorla</span>
                </label>
              </div>
              {!targetDraft.productId && scopedProducts.length > 0 ? (
                <ProductSelectionList
                  products={scopedProducts}
                  selectedIds={selectedProductIds}
                  onToggle={handleToggleProductSelection}
                  onSelectAll={handleSelectAllProducts}
                  onClearAll={handleClearAllProducts}
                />
              ) : null}
              <div className="form-actions">
                <button className="button secondary" type="submit">
                  Hedef ekle
                </button>
              </div>
            </form>
          </div>
        </>
      ) : (
        <div className="option-section muted">
          <p className="helper-text">
            Seçenekleri ve hedefleri tanımlamak için önce grubu kaydedin.
          </p>
        </div>
      )}
    </div>
  );
}
